/**
 * Client-side gate for the artifact upload pipeline. Mirrors the BE
 * limits (`modules/artifacts/upload_limits.py`) so the user gets
 * immediate feedback on the drop instead of round-tripping for an obvious
 * "too large / wrong type" error.
 *
 * The BE is still authoritative — it re-checks every constraint, plus
 * page-count for PDFs which we can't read from the FE without parsing.
 * Any 400 response from the upload mutation should be surfaced via
 * `formatUploadLimitError` for a consistent message.
 */

import type {
  ArtifactDuplicateEntry,
  ArtifactSummaryResponse,
  ArtifactUploadConstraintsResponse,
} from '@store/generated/artifacts';

/** Sensible defaults if the constraints query hasn't resolved yet —
 *  matches the BE constants so we don't gate too loosely. */
export const DEFAULT_UPLOAD_CONSTRAINTS: ArtifactUploadConstraintsResponse = {
  allowed_content_types: [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  max_file_size_bytes: 25 * 1024 * 1024,
  max_pdf_pages: 100,
};

export interface RejectedFile {
  file: File;
  reason: string;
}

export interface ValidationResult {
  accepted: File[];
  rejected: RejectedFile[];
}

/**
 * Resolve the file's MIME type, falling back to its extension when the
 * browser hands us `application/octet-stream` (or empty). The BE rejects
 * `application/octet-stream`, so we have to set a concrete type on the
 * `FormData` part — see `prepareFileForUpload`.
 */
export function resolveContentType(file: File): string {
  if (file.type && file.type !== 'application/octet-stream') return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    default:
      return file.type || '';
  }
}

/**
 * Re-wrap the file with a concrete MIME type when needed, so the upload
 * mutation's `FormData.append('files', file)` sets the correct
 * `Content-Type` on the part. Browsers vary in how reliably they
 * populate `file.type`; this normalizes the wire format.
 */
export function prepareFileForUpload(file: File): File {
  const resolved = resolveContentType(file);
  if (file.type === resolved) return file;
  return new File([file], file.name, { type: resolved, lastModified: file.lastModified });
}

/**
 * HEIC/HEIF is what iPhones save photos as by default, and the BE
 * deliberately doesn't accept it — `image/heic` isn't in the allowlist and
 * there's no server-side conversion. The generic "isn't a supported file
 * type" message reads like a bug report to a user who just took a photo, so
 * this file is named specifically and pointed at a fix. Checked by both
 * extension and MIME type, since browsers are inconsistent about setting
 * `file.type` for HEIC (Safari does, Chrome on some platforms leaves it
 * empty).
 */
function isHeicFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'heic' || ext === 'heif') return true;
  const type = file.type.toLowerCase();
  return type === 'image/heic' || type === 'image/heif';
}

export function validateFiles(
  files: File[],
  constraints: ArtifactUploadConstraintsResponse,
): ValidationResult {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];

  for (const file of files) {
    const type = resolveContentType(file);
    if (!type || !constraints.allowed_content_types.includes(type)) {
      if (isHeicFile(file)) {
        rejected.push({
          file,
          reason: `${file.name} is a HEIC photo, which isn't supported yet. Save it as a JPG or PDF and upload that instead.`,
        });
        continue;
      }
      rejected.push({
        file,
        reason: `${file.name} isn't a supported file type (${type || 'unknown'}). ${formatAllowedTypes(constraints.allowed_content_types)}.`,
      });
      continue;
    }
    if (file.size > constraints.max_file_size_bytes) {
      rejected.push({
        file,
        reason: `${file.name} is too large (${formatBytes(file.size)}). Max is ${formatBytes(constraints.max_file_size_bytes)}.`,
      });
      continue;
    }
    accepted.push(file);
  }

  return { accepted, rejected };
}

/**
 * Friendly prose for the constraint summary, suitable for the drop
 * overlay subtitle and tooltip. e.g. "PDF, JPG, PNG, GIF, WebP".
 */
export function formatAllowedTypes(types: string[]): string {
  const labels = types.map(t => CONTENT_TYPE_LABEL[t] ?? t).filter(Boolean);
  if (labels.length === 0) return '';
  return labels.join(', ');
}

const CONTENT_TYPE_LABEL: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
};

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * BE 400 envelope for upload-limit violations. Per-error_code `details`
 * payload is loosely typed; we only key off `message` and `error_code`.
 */
export interface ArtifactUploadLimitError {
  status?: number;
  error_code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Unwrap the BE response body from an RTK Query error.
 *
 * Our `auth0BaseQuery` double-wraps: it transforms every fetch error
 * into a `CustomErrorResponse` shape and stuffs that on `error.data`,
 * so the original BE body ends up at `error.data.data`. We accept both
 * shapes so the helpers below work whether or not the wrapper is
 * present (e.g. after a future baseQuery refactor, or for endpoints
 * that bypass it).
 */
function unwrapResponseBody(error: unknown): unknown {
  if (!error || typeof error !== 'object') return null;
  const top = (error as { data?: unknown }).data;
  if (!top || typeof top !== 'object') return null;
  // Inner envelope: auth0BaseQuery's CustomErrorResponse stashes the
  // real body at .data.
  if ('data' in top && typeof (top as { data: unknown }).data === 'object') {
    return (top as { data: unknown }).data;
  }
  return top;
}

/**
 * Pull a friendly message out of an RTK Query error response. Prefers
 * the BE's own `message` field over the auth0BaseQuery wrapper's
 * generic "Unknown error occurred" fallback.
 */
export function formatUploadLimitError(error: unknown): string {
  const body = unwrapResponseBody(error) as ArtifactUploadLimitError | null;
  if (body?.message) return body.message;
  return 'Upload failed. Try again.';
}

/**
 * Unified outcome for an artifact upload. Wraps both happy-path 200
 * responses and 409 conflict responses (where some files succeeded and
 * some collided with existing artifacts) into one shape so callers can
 * track ghosts for `artifacts` and surface friendly messages for
 * `duplicates` without branching on status code.
 */
export interface UploadOutcome {
  artifacts: ArtifactSummaryResponse[];
  duplicates: ArtifactDuplicateEntry[];
  artifact_batch_id?: string | null;
}

interface ConflictResponseBody {
  artifacts?: ArtifactSummaryResponse[];
  duplicates?: ArtifactDuplicateEntry[];
  artifact_batch_id?: string | null;
}

/**
 * Detect a 409-style conflict response on a thrown RTK Query error and
 * return it as a normalized UploadOutcome. Returns `null` for anything
 * else so the caller can re-throw genuine errors.
 *
 * The BE sends `{artifacts: [], duplicates: [...]}` on conflict (status
 * 409 per the GDN-1032 contract). We accept any shape that carries a
 * `duplicates` array, in case the status varies.
 */
export function parseConflictResponse(error: unknown): UploadOutcome | null {
  const body = unwrapResponseBody(error) as ConflictResponseBody | null;
  if (!body || !Array.isArray(body.duplicates)) return null;
  return {
    artifacts: body.artifacts ?? [],
    duplicates: body.duplicates,
    artifact_batch_id: body.artifact_batch_id ?? null,
  };
}

/**
 * Friendly message for a single duplicate entry. The BE's `message`
 * field is technically descriptive but reads like a server log — we
 * write our own copy in the user's voice.
 */
export function formatDuplicateMessage(
  entry: ArtifactDuplicateEntry,
  scope: 'patient' | 'organization' = 'patient',
): string {
  const where = scope === 'organization' ? 'your organization' : 'this chart';
  return `${entry.filename} is already in ${where} — skipped duplicate`;
}
